import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MOCK_ORDERS, MockOrder } from '../mock-data/mock-orders.data';

/**
 * Column Filters Example
 * Demonstrates Excel-like column filters with checkbox lists
 */
@Component({
  selector: 'app-filters-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Filtros</h2>
        <p class="text-gray-600 mb-2">
          Grid con filtros tipo Excel por columna. Haz clic en el ícono de filtro
          en cada columna para ver las opciones disponibles.
        </p>
        <div class="flex gap-2 flex-wrap">
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            Filtros por Columna
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Multi-select
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            Búsqueda en filtros
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            Contador de registros
          </span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          [data]="orders"
          [columnDefs]="columnDefs"
          [config]="gridConfig">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">💡 Cómo usar los filtros:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>Haz clic en el ícono de filtro en el header de cualquier columna</li>
          <li>Selecciona o deselecciona los valores que deseas ver</li>
          <li>Usa "Seleccionar Todo" para seleccionar/deseleccionar todos</li>
          <li>Usa la búsqueda para encontrar valores específicos (si hay más de 5 opciones)</li>
          <li>Haz clic en "Aplicar" para aplicar el filtro</li>
          <li>Haz clic en "Limpiar" para quitar el filtro de esa columna</li>
          <li>Los números entre paréntesis muestran cuántos registros tienen ese valor</li>
          <li>El ícono de filtro se destaca cuando hay un filtro activo en la columna</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>
    </div>
  `,
})
export class FiltersExampleComponent {
  orders: MockOrder[] = MOCK_ORDERS;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
      filterable: true,  // ✨ Enable filter
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 250,
      filterable: true,  // ✨ Enable filter
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 180,
      filterable: true,  // ✨ Enable filter
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 150,
      filterable: true,  // ✨ Enable filter
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toFixed(2)}`,
      filterable: false,  // Disable filter for this column
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 150,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
      filterable: false,  // Disable filter for this column
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 20,
    },
    sorting: {
      enabled: true,
      multiColumn: true,
      maxMultiSortColumns: 3,
    },
    columnFilters: {
      enabled: true,          // ✨ Enable column filters
      mode: 'local',         // Local filtering
      showFilterIcon: true,  // Show filter icon in headers
      trigger: 'click',      // Open filter menu on click
    },
    styling: {
      maxHeight: '600px',
      stripedRows: true,
    },
  };

  exampleCode = `// Component Code
columnDefs: ColDef<MockOrder>[] = [
  {
    field: 'soldTo.name',
    headerName: 'Cliente',
    filterable: true  // ✨ Enable filter for this column
  },
  {
    field: 'class.name',
    headerName: 'Clase',
    filterable: true
  },
  {
    field: 'total',
    headerName: 'Total',
    filterable: false  // Disable filter
  }
];

gridConfig: GridConfig<MockOrder> = {
  columnFilters: {
    enabled: true,          // ✨ Enable column filters globally
    mode: 'local',         // Use local data for filters
    showFilterIcon: true,  // Show filter icon
    trigger: 'click'       // Open on click
  },
  pagination: { enabled: true, pageSize: 20 },
  sorting: { enabled: true, multiColumn: true }
};

// Template
<app-smart-grid
  [data]="orders"
  [columnDefs]="columnDefs"
  [config]="gridConfig">
</app-smart-grid>

// Features:
// - Excel-like filters with checkbox lists
// - Multi-select with Select All/Clear
// - Search within filter options
// - Record counts for each value
// - Visual indicator when filter is active
// - Combines with sorting and pagination`;
}
